import { AssessmentStatus } from '@libs/constants';
import { SingularKeys, useTrans } from '@libs/i18n';

import { BadgeVariant } from '../../../../components/Badge';

export const useAssessmentUtils = () => {
  const { t } = useTrans();

  const getStatusTranslationKey = (status: AssessmentStatus): SingularKeys => {
    const keys: Record<AssessmentStatus, SingularKeys> = {
      [AssessmentStatus.DRAFT]: 'app.assessments.status.draft',
      [AssessmentStatus.SUBMITTED]: 'app.assessments.status.submitted',
      [AssessmentStatus.PROCESSING]: 'app.assessments.status.processing',
      [AssessmentStatus.COMPLETED]: 'app.assessments.status.completed',
      [AssessmentStatus.FAILED]: 'app.assessments.status.failed',
    };
    return keys[status];
  };


  const getAssessmentStatusVariant = (status: AssessmentStatus): BadgeVariant => {
    const variants: Record<AssessmentStatus, BadgeVariant> = {
      [AssessmentStatus.DRAFT]: 'Draft',
      [AssessmentStatus.SUBMITTED]: 'Submitted',
      [AssessmentStatus.PROCESSING]: 'Processing',
      [AssessmentStatus.COMPLETED]: 'Completed',
      [AssessmentStatus.FAILED]: 'Failed',
    };
    return variants[status] || 'neutral';
  };


  const formatUpdatedTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      if (diffInMinutes < 1) {
        return t('app.assessments.time.now');
      }
      return t('app.assessments.time.minutesAgo', { count: diffInMinutes });
    }
    if (diffInHours < 24) {
      return t('app.assessments.time.hoursAgo', { count: diffInHours });
    }
    return date.toLocaleDateString();
  };

  const formatEvaluationDuration = (
    submittedAt: string | null | undefined,
    completedAt: string | null | undefined,
  ) => {
    if (!submittedAt || !completedAt) return '—';

    const durationMs = new Date(completedAt).getTime() - new Date(submittedAt).getTime();
    if (durationMs < 0) return '—';

    const totalMinutes = Math.floor(durationMs / (1000 * 60));
    const totalHours = Math.floor(durationMs / (1000 * 60 * 60));
    const totalDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));

    if (totalMinutes < 1) {
      return t('app.assessments.duration.lessThanMinute');
    }
    if (totalHours < 1) {
      return t('app.assessments.duration.minutes', { count: totalMinutes });
    }
    if (totalDays < 1) {
      const minutes = totalMinutes % 60;
      if (minutes === 0) {
        return t('app.assessments.duration.hours', { count: totalHours });
      }
      return t('app.assessments.duration.hoursMinutes', {
        hours: totalHours,
        minutes,
      });
    }
    return t('app.assessments.duration.days', { count: totalDays });
  };

  return {
    getStatusTranslationKey,
    getAssessmentStatusVariant,
    formatUpdatedTime,
    formatEvaluationDuration,
  };
};
